import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  LogOut,
  MessageCircle,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const WHATSAPP_NUMBER = "966504838870";

type Alert = {
  id: string;
  alert_type: string;
  severity: "low" | "medium" | "high";
  page_url: string | null;
  user_agent: string | null;
  ip_hash: string | null;
  session_id: string | null;
  details: Record<string, unknown> | null;
  notified: boolean;
  created_at: string;
};

const severityVariant = (s: Alert["severity"]) => {
  if (s === "high") return "destructive" as const;
  if (s === "medium") return "default" as const;
  return "secondary" as const;
};

const formatAlertType = (t: string) =>
  t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const buildWhatsAppMessage = (a: Alert) => {
  const lines = [
    `🚨 Arabian Al Waha — Website Security Alert`,
    ``,
    `Type: ${formatAlertType(a.alert_type)}`,
    `Severity: ${a.severity.toUpperCase()}`,
    `Time: ${new Date(a.created_at).toLocaleString()}`,
    a.page_url ? `Page: ${a.page_url}` : null,
    a.ip_hash ? `Visitor (hash): ${a.ip_hash}` : null,
    a.user_agent ? `Device: ${a.user_agent.slice(0, 120)}` : null,
  ].filter(Boolean);
  return encodeURIComponent(lines.join("\n"));
};

const AdminSecurity = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("security_alerts")
      .select(
        "id, alert_type, severity, page_url, user_agent, ip_hash, session_id, details, notified, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) {
      toast.error("Could not load alerts. You may not have admin access.");
      setAlerts([]);
    } else {
      setAlerts((data ?? []) as Alert[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate("/admin", { replace: true });
        return;
      }
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", sessionData.session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!mounted) return;
      if (!roles) {
        toast.error(
          "Your account is not an admin. Ask the owner to grant access.",
        );
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      load();
    })();
    return () => {
      mounted = false;
    };
  }, [navigate, load]);

  // Poll for new alerts every 30s (realtime channel removed for security:
  // security_alerts is no longer published to realtime so it cannot be
  // subscribed to by non-admin users).
  useEffect(() => {
    if (!authorized) return;
    const interval = setInterval(() => {
      load();
    }, 30000);
    return () => clearInterval(interval);
  }, [authorized, load]);

  const stats = useMemo(() => {
    if (!alerts)
      return { total: 0, high: 0, medium: 0, low: 0, unnotified: 0 };
    return {
      total: alerts.length,
      high: alerts.filter((a) => a.severity === "high").length,
      medium: alerts.filter((a) => a.severity === "medium").length,
      low: alerts.filter((a) => a.severity === "low").length,
      unnotified: alerts.filter((a) => !a.notified).length,
    };
  }, [alerts]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/admin", { replace: true });
  };

  const sendToWhatsApp = (a: Alert) => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppMessage(a)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    // Mark as notified
    supabase
      .from("security_alerts")
      .update({ notified: true })
      .eq("id", a.id)
      .then(({ error }) => {
        if (!error) {
          setAlerts((prev) =>
            prev
              ? prev.map((x) => (x.id === a.id ? { ...x, notified: true } : x))
              : prev,
          );
        }
      });
  };

  const sendAllUnnotifiedToWhatsApp = () => {
    const unnotified = (alerts ?? []).filter((a) => !a.notified).slice(0, 10);
    if (unnotified.length === 0) {
      toast.info("No unnotified alerts");
      return;
    }
    const summary = [
      `🚨 Arabian Al Waha — ${unnotified.length} new website security alerts`,
      ``,
      ...unnotified.map(
        (a, i) =>
          `${i + 1}. ${formatAlertType(a.alert_type)} (${a.severity}) — ${new Date(a.created_at).toLocaleString()}`,
      ),
    ].join("\n");
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(summary)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    const ids = unnotified.map((a) => a.id);
    supabase
      .from("security_alerts")
      .update({ notified: true })
      .in("id", ids)
      .then(({ error }) => {
        if (!error) {
          setAlerts((prev) =>
            prev
              ? prev.map((x) => (ids.includes(x.id) ? { ...x, notified: true } : x))
              : prev,
          );
        }
      });
  };

  return (
    <main className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-10 max-w-6xl">
        <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
              <ShieldAlert className="w-7 h-7" /> Security Alerts
            </h1>
            <p className="text-muted-foreground">
              Suspicious visitor activity on arabianalwaha.com
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={load}
              disabled={loading || !authorized}
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="ghost" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </header>

        {!authorized && !loading && (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <p className="text-muted-foreground">
              You're signed in but not an admin. Ask the site owner to add your
              user ID to the admin role.
            </p>
          </div>
        )}

        {authorized && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
              <StatCard label="Total" value={stats.total} />
              <StatCard label="High" value={stats.high} tone="destructive" />
              <StatCard label="Medium" value={stats.medium} tone="warning" />
              <StatCard label="Low" value={stats.low} />
              <StatCard label="Unnotified" value={stats.unnotified} tone="warning" />
            </div>

            <div className="flex justify-end mb-4">
              <Button
                onClick={sendAllUnnotifiedToWhatsApp}
                disabled={stats.unnotified === 0}
                className="bg-[#25D366] hover:bg-[#1ebe57] text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Send {stats.unnotified} new to WhatsApp
              </Button>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Page</TableHead>
                      <TableHead>Visitor</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-muted-foreground py-8"
                        >
                          Loading…
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && alerts && alerts.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-muted-foreground py-8"
                        >
                          No alerts yet — your site is quiet.
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading &&
                      alerts?.map((a) => (
                        <TableRow key={a.id} className={a.notified ? "opacity-60" : ""}>
                          <TableCell className="whitespace-nowrap text-sm">
                            {new Date(a.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {a.notified ? (
                                <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                              )}
                              {formatAlertType(a.alert_type)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={severityVariant(a.severity)}>
                              {a.severity}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className="max-w-[220px] truncate text-xs"
                            title={a.page_url ?? ""}
                          >
                            {a.page_url ?? "—"}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {a.ip_hash ?? "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant={a.notified ? "outline" : "default"}
                              onClick={() => sendToWhatsApp(a)}
                              className={
                                a.notified
                                  ? ""
                                  : "bg-[#25D366] hover:bg-[#1ebe57] text-white"
                              }
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              {a.notified ? "Resend" : "WhatsApp"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
              <Bell className="w-3 h-3" />
              Page auto-refreshes when new alerts arrive. Click "WhatsApp" on
              any row to forward it to {`+${WHATSAPP_NUMBER}`}.
            </p>
          </>
        )}
      </div>
    </main>
  );
};

const StatCard = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "destructive" | "warning";
}) => (
  <div className="bg-card border border-border rounded-xl p-5">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p
      className={`text-2xl font-bold mt-1 ${
        tone === "destructive"
          ? "text-destructive"
          : tone === "warning"
            ? "text-amber-500"
            : "text-primary"
      }`}
    >
      {value}
    </p>
  </div>
);

export default AdminSecurity;